// ── JobSpec Execution ──
// Per-job, per-phase execution state driven by the bound JobSpec.
// Installers fill in photos + checklist items; phases gate forward.
// QA reviews submitted phases and PASS/FAILs them.
//
// This is a NEW execution layer. The legacy QAForm at /qa-form remains
// untouched (non-destructive beta). New work flows through this model.

import type {
  BoundJobSpec,
  JobSpec,
  JobSpecPhase,
  JobSpecPhaseName,
  RequiredPhotoType,
} from "./jobSpec";
import { resolveBoundSpec } from "./jobSpec";
import { isKitGateCleared, type MaterialKit } from "./materialKit";

// Backward-compatible default:
// - false: missing reconciliation does NOT block next phases
// - true: demo reconciliation is strictly required before advancing
const ENFORCE_DEMO_RECONCILIATION =
  (import.meta.env.VITE_ENFORCE_DEMO_RECONCILIATION ?? "false") === "true";

// Backward-compatible default:
// - false: install phase does NOT hard-require kit confirmation
// - true: install phase is blocked until kit is confirmed (or PM override)
const ENFORCE_INSTALL_KIT_GATE =
  (import.meta.env.VITE_ENFORCE_INSTALL_KIT_GATE ?? "false") === "true";

export type PhaseStatus =
  | "not_started"     // installer hasn't touched
  | "in_progress"     // some photos/checks recorded
  | "submitted"       // installer marked complete, awaiting QA
  | "qa_passed"       // QA approved
  | "qa_failed";      // QA rejected — returned to installer

export interface PhotoUpload {
  photoType: RequiredPhotoType;
  url: string;          // mock filename in this preview
  uploadedAt: string;   // ISO
  attachmentId?: string;
}

export interface ChecklistResponse {
  itemId: string;
  checked: boolean;
  checkedAt?: string;
}

export interface PhaseExecution {
  phaseName: JobSpecPhaseName;
  status: PhaseStatus;
  photos: PhotoUpload[];
  checklist: ChecklistResponse[];
  /** Set when installer marks phase complete (gated by photos+checks). */
  completedAt?: string;
  completedBy?: string;
  /** QA outcome metadata. */
  qaReviewedAt?: string;
  qaReviewer?: string;
  qaFailReason?: string;
  /**
   * PM reconciliation gate. Currently only the Demo phase requires this.
   * Set after the phase is `qa_passed`. Until decision === "spec_valid",
   * the next phase remains locked. Captures clean deviation data.
   */
  reconciliation?: PhaseReconciliation;
}

// ── Reconciliation Gate ───────────────────────────────────────────────────
// Prevents silent field improvisation after Demo. PM must explicitly mark
// the bound spec as still valid (or flag a deviation) before the next phase
// can begin. Stored on PhaseExecution so the gate survives multi-archetype
// specs and additional future gates (e.g. post-plumbing rough-in).

export type DeviationReason =
  | "drain_misalignment"
  | "subfloor_damage"
  | "wall_out_of_square"
  | "plumbing_mismatch"
  | "material_issue"
  | "customer_change"
  | "other";

export const DEVIATION_REASONS: { id: DeviationReason; label: string }[] = [
  { id: "drain_misalignment", label: "Drain misalignment" },
  { id: "subfloor_damage", label: "Subfloor damage" },
  { id: "wall_out_of_square", label: "Wall out of square" },
  { id: "plumbing_mismatch", label: "Plumbing mismatch" },
  { id: "material_issue", label: "Material issue" },
  { id: "customer_change", label: "Customer change" },
  { id: "other", label: "Other" },
];

export type ReconciliationDecision = "spec_valid" | "deviation_required";

export interface PhaseReconciliation {
  decision: ReconciliationDecision;
  reason?: DeviationReason;
  notes?: string;
  decidedAt: string;
  decidedBy: string;
}

/** Phases that require PM reconciliation after QA passes. */
const PHASES_REQUIRING_RECONCILIATION: ReadonlySet<JobSpecPhaseName> = new Set(["demo"]);

export function phaseRequiresReconciliation(phaseName: JobSpecPhaseName): boolean {
  return PHASES_REQUIRING_RECONCILIATION.has(phaseName);
}

/** A reconciliation prompt should appear when the phase is qa_passed and not yet resolved as spec_valid. */
export function isReconciliationPending(phase: PhaseExecution): boolean {
  if (!phaseRequiresReconciliation(phase.phaseName)) return false;
  if (phase.status !== "qa_passed") return false;
  if (!ENFORCE_DEMO_RECONCILIATION) return false;
  return phase.reconciliation?.decision !== "spec_valid";
}

/** Phase-gate predicate: the reconciliation has cleared this phase to advance. */
export function isReconciliationCleared(phase: PhaseExecution): boolean {
  if (!phaseRequiresReconciliation(phase.phaseName)) return true;
  if (!ENFORCE_DEMO_RECONCILIATION) return true;
  return phase.reconciliation?.decision === "spec_valid";
}

export interface JobSpecExecution {
  jobId: string;
  specRef: { specId: string; versionNumber: number };
  phases: PhaseExecution[];
  /** True once ALL phases reach qa_passed. */
  allPhasesComplete: boolean;
}

// ── Factory / lookups ─────────────────────────────────────────────────────

export function createExecutionForSpec(jobId: string, spec: JobSpec): JobSpecExecution {
  return {
    jobId,
    specRef: { specId: spec.specId, versionNumber: spec.versionNumber },
    phases: spec.phases.map((p) => ({
      phaseName: p.name,
      status: "not_started",
      photos: [],
      checklist: p.checklistItems.map((c) => ({ itemId: c.id, checked: false })),
    })),
    allPhasesComplete: false,
  };
}

export function getExecution(
  executions: JobSpecExecution[],
  jobId: string,
): JobSpecExecution | undefined {
  return executions.find((e) => e.jobId === jobId);
}

/** Ensure an execution record exists for the given bound spec. */
export function ensureExecution(
  executions: JobSpecExecution[],
  jobId: string,
  bound: BoundJobSpec,
): { executions: JobSpecExecution[]; execution: JobSpecExecution } {
  const existing = getExecution(executions, jobId);
  if (existing) return { executions, execution: existing };
  const spec = resolveBoundSpec(bound);
  if (!spec) throw new Error(`Cannot ensure execution: spec ${bound.specId}-v${bound.versionNumber} not found`);
  const fresh = createExecutionForSpec(jobId, spec);
  return { executions: [...executions, fresh], execution: fresh };
}

// ── Gating ────────────────────────────────────────────────────────────────

export function isPhaseReadyToSubmit(phase: PhaseExecution, specPhase: JobSpecPhase): boolean {
  const allChecked = specPhase.checklistItems.every((item) =>
    phase.checklist.find((c) => c.itemId === item.id)?.checked,
  );
  const allPhotos = specPhase.requiredPhotos.every((pt) =>
    phase.photos.some((p) => p.photoType === pt),
  );
  return allChecked && allPhotos;
}

/**
 * Determines which phase is currently active for the installer.
 * Phases are strictly sequential (demo → plumbing → install → qa).
 * The first phase that is NOT qa_passed becomes the active one.
 */
export function getActivePhase(execution: JobSpecExecution, spec: JobSpec): JobSpecPhase | undefined {
  for (const specPhase of spec.phases) {
    const phase = execution.phases.find((p) => p.phaseName === specPhase.name);
    if (!phase || phase.status !== "qa_passed") return specPhase;
  }
  return undefined;
}

export function isPhaseUnlocked(
  execution: JobSpecExecution,
  spec: JobSpec,
  phaseName: JobSpecPhaseName,
  /**
   * Optional kit gate — only consulted for the install phase. If the install
   * phase is requested and a kit is provided, install is locked until the
   * kit gate clears (`isKitGateCleared`). Phases other than install ignore
   * this argument.
   */
  kit?: MaterialKit,
  /**
   * Optional material alignment predicate — only consulted for the install
   * phase. When provided AND false, install is locked even if the kit gate
   * itself clears, because the kit/PO/spec are out of sync. Callers that
   * don't yet pass this argument retain pre-alignment-gate behavior.
   */
  aligned?: boolean,
): boolean {
  const idx = spec.phases.findIndex((p) => p.name === phaseName);
  if (idx > 0) {
    // All prior phases must be qa_passed AND have any required reconciliation
    // cleared (decision === "spec_valid"). Phases that don't require
    // reconciliation pass that predicate trivially.
    for (let i = 0; i < idx; i++) {
      const prior = execution.phases.find((p) => p.phaseName === spec.phases[i].name);
      if (!prior || prior.status !== "qa_passed") return false;
      if (!isReconciliationCleared(prior)) return false;
    }
  }
  if (phaseName === "install") {
    // Single shared predicate — same one dispatch uses.
    if (ENFORCE_INSTALL_KIT_GATE && !isKitGateCleared(kit)) return false;
    // Material alignment — when caller passes the verdict, enforce it.
    // Treat "not provided" as legacy/passing so older call sites don't break.
    if (aligned === false) return false;
  }
  return true;
}

// ── State transitions (pure) ──────────────────────────────────────────────

function updatePhase(
  execution: JobSpecExecution,
  phaseName: JobSpecPhaseName,
  patch: (p: PhaseExecution) => PhaseExecution,
): JobSpecExecution {
  return {
    ...execution,
    phases: execution.phases.map((p) => (p.phaseName === phaseName ? patch(p) : p)),
  };
}

export function recordPhoto(
  execution: JobSpecExecution,
  phaseName: JobSpecPhaseName,
  photoType: RequiredPhotoType,
  now: Date = new Date(),
): JobSpecExecution {
  return updatePhase(execution, phaseName, (p) => {
    if (p.status === "qa_passed") return p; // locked
    const otherPhotos = p.photos.filter((ph) => ph.photoType !== photoType);
    return {
      ...p,
      status: p.status === "not_started" ? "in_progress" : p.status,
      photos: [
        ...otherPhotos,
        { photoType, url: `photo_${phaseName}_${photoType}_${now.getTime()}.jpg`, uploadedAt: now.toISOString() },
      ],
    };
  });
}

export function toggleChecklist(
  execution: JobSpecExecution,
  phaseName: JobSpecPhaseName,
  itemId: string,
  checked: boolean,
  now: Date = new Date(),
): JobSpecExecution {
  return updatePhase(execution, phaseName, (p) => {
    if (p.status === "qa_passed") return p;
    return {
      ...p,
      status: p.status === "not_started" ? "in_progress" : p.status,
      checklist: p.checklist.map((c) =>
        c.itemId === itemId
          ? { ...c, checked, checkedAt: checked ? now.toISOString() : undefined }
          : c,
      ),
    };
  });
}

export type SubmitResult =
  | { ok: true; execution: JobSpecExecution }
  | { ok: false; reason: string };

export function submitPhase(
  execution: JobSpecExecution,
  spec: JobSpec,
  phaseName: JobSpecPhaseName,
  installerName: string,
  now: Date = new Date(),
  /** Optional kit — required for the install phase to clear the kit gate. */
  kit?: MaterialKit,
  /** Optional alignment verdict — required for install when caller can compute it. */
  aligned?: boolean,
): SubmitResult {
  const specPhase = spec.phases.find((p) => p.name === phaseName);
  if (!specPhase) return { ok: false, reason: `Phase ${phaseName} not in spec` };

  if (!isPhaseUnlocked(execution, spec, phaseName, kit, aligned)) {
    if (phaseName === "install") {
      if (aligned === false) {
        return {
          ok: false,
          reason:
            "Install is locked. Materials are out of sync (spec / order / kit). Resolve the mismatch before installing.",
        };
      }
      return {
        ok: false,
        reason:
          ENFORCE_INSTALL_KIT_GATE
            ? "Install is locked. Prior phases must pass QA + reconciliation, AND the Material Kit must be confirmed (or PM override logged)."
            : "Install is locked. Prior phases must pass QA + reconciliation.",
      };
    }
    return { ok: false, reason: "Prior phases must pass QA before this phase can be submitted." };
  }

  const phase = execution.phases.find((p) => p.phaseName === phaseName);
  if (!phase) return { ok: false, reason: `Phase ${phaseName} missing in execution` };
  if (phase.status === "qa_passed") return { ok: false, reason: "Phase already passed QA." };

  if (!isPhaseReadyToSubmit(phase, specPhase)) {
    return {
      ok: false,
      reason: "All required photos and checklist items must be completed before submitting.",
    };
  }

  // NOTE: do NOT clear qaFailReason here. It is preserved as an "ever failed"
  // tombstone so SpecExecutionOutcome derivation can correctly count rework
  // and detect non-first-pass phases even after a later successful QA.
  const next = updatePhase(execution, phaseName, (p) => ({
    ...p,
    status: "submitted",
    completedAt: now.toISOString(),
    completedBy: installerName,
  }));
  return { ok: true, execution: next };
}

export function qaPassPhase(
  execution: JobSpecExecution,
  phaseName: JobSpecPhaseName,
  reviewer: string,
  now: Date = new Date(),
): SubmitResult {
  const phase = execution.phases.find((p) => p.phaseName === phaseName);
  if (!phase) return { ok: false, reason: `Phase ${phaseName} missing` };
  if (phase.status !== "submitted") {
    return { ok: false, reason: "Only submitted phases can be reviewed." };
  }
  // NOTE: qaFailReason is intentionally preserved on pass — it acts as an
  // "ever failed" tombstone for outcome derivation (phaseQaFirstPass /
  // qaFailureCount / reworkLoopsByPhase). Clearing it would silently
  // under-report rework in the SpecExecutionOutcome dataset.
  const next = updatePhase(execution, phaseName, (p) => ({
    ...p,
    status: "qa_passed",
    qaReviewedAt: now.toISOString(),
    qaReviewer: reviewer,
  }));
  const allPassed = next.phases.every((p) => p.status === "qa_passed");
  return { ok: true, execution: { ...next, allPhasesComplete: allPassed } };
}

export function qaFailPhase(
  execution: JobSpecExecution,
  phaseName: JobSpecPhaseName,
  reviewer: string,
  reason: string,
  now: Date = new Date(),
): SubmitResult {
  const phase = execution.phases.find((p) => p.phaseName === phaseName);
  if (!phase) return { ok: false, reason: `Phase ${phaseName} missing` };
  if (phase.status !== "submitted") {
    return { ok: false, reason: "Only submitted phases can be reviewed." };
  }
  if (!reason.trim()) return { ok: false, reason: "Fail reason is required." };
  const next = updatePhase(execution, phaseName, (p) => ({
    ...p,
    status: "qa_failed",
    qaReviewedAt: now.toISOString(),
    qaReviewer: reviewer,
    qaFailReason: reason.trim(),
    completedAt: undefined,
    completedBy: undefined,
  }));
  return { ok: true, execution: { ...next, allPhasesComplete: false } };
}

// ── Reconciliation action ─────────────────────────────────────────────────

const OTHER_NOTES_MIN_LENGTH = 20;

export interface ReconcileInput {
  decision: ReconciliationDecision;
  reason?: DeviationReason;
  notes?: string;
  decidedBy: string;
}

export function reconcilePhase(
  execution: JobSpecExecution,
  phaseName: JobSpecPhaseName,
  input: ReconcileInput,
  now: Date = new Date(),
): SubmitResult {
  if (!phaseRequiresReconciliation(phaseName)) {
    return { ok: false, reason: `Phase ${phaseName} does not require reconciliation.` };
  }

  const phase = execution.phases.find((p) => p.phaseName === phaseName);
  if (!phase) return { ok: false, reason: `Phase ${phaseName} missing in execution.` };
  if (phase.status !== "qa_passed") {
    return { ok: false, reason: "Phase must pass QA before it can be reconciled." };
  }
  if (!input.decidedBy.trim()) {
    return { ok: false, reason: "PM name is required." };
  }

  if (input.decision === "deviation_required") {
    if (!input.reason) return { ok: false, reason: "Deviation reason is required." };
    const notes = (input.notes ?? "").trim();
    if (!notes) return { ok: false, reason: "Deviation notes are required." };
    if (input.reason === "other" && notes.length < OTHER_NOTES_MIN_LENGTH) {
      return {
        ok: false,
        reason: `"Other" requires meaningful detail (min ${OTHER_NOTES_MIN_LENGTH} characters).`,
      };
    }
  }

  const reconciliation: PhaseReconciliation = {
    decision: input.decision,
    reason: input.decision === "deviation_required" ? input.reason : undefined,
    notes: input.decision === "deviation_required" ? input.notes?.trim() : undefined,
    decidedAt: now.toISOString(),
    decidedBy: input.decidedBy.trim(),
  };

  const next = updatePhase(execution, phaseName, (p) => ({ ...p, reconciliation }));
  return { ok: true, execution: next };
}

// ── Status helpers (display) ──────────────────────────────────────────────

export function phaseProgressPct(phase: PhaseExecution, specPhase: JobSpecPhase): number {
  const totalChecks = specPhase.checklistItems.length;
  const totalPhotos = specPhase.requiredPhotos.length;
  const total = totalChecks + totalPhotos;
  if (total === 0) return 100;
  const checks = phase.checklist.filter((c) => c.checked).length;
  const photos = specPhase.requiredPhotos.filter((pt) =>
    phase.photos.some((p) => p.photoType === pt),
  ).length;
  return Math.round(((checks + photos) / total) * 100);
}
